"use client";

import {
	type PointerEvent as ReactPointerEvent,
	type RefObject,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";

/** Progress within this band clears the post-commit playhead latch. */
export const MEDIA_PLAYER_SCRUB_LATCH_EPSILON = 0.35;

export function clampMediaPlayerSeekPercent(
	requestedPercent: number,
	maxSeekPercent: number,
): { clampedPercent: number; wasRejected: boolean } {
	const max = Math.max(0, Math.min(100, maxSeekPercent));
	const requested = Math.max(0, Math.min(100, requestedPercent));
	if (requested <= max) {
		return { clampedPercent: requested, wasRejected: false };
	}
	return { clampedPercent: max, wasRejected: true };
}

export interface UseMediaPlayerScrubOptions {
	progressPercent: number;
	maxSeekPercent: number;
	/** Effective max for drag/commit; defaults to maxSeekPercent when omitted. */
	scrubClampPercent?: number;
	interactive: boolean;
	/**
	 * Bumps when an external transport seek lands (session seekEpoch).
	 * Clears the post-commit latch so the playhead follows live progress.
	 */
	seekEpoch?: number;
	/** Full track element used to map pointer X → percent when the hit target is narrower. */
	trackBoundsRef?: RefObject<HTMLElement | null>;
	onPause: () => void;
	onSeekCommit: (percent: number) => void;
	onSeekPreview?: (percent: number) => void;
	onSeekRejected?: () => void;
}

export interface UseMediaPlayerScrubResult {
	/** Playhead for indicator, glow, and thumb (scrub preview when dragging). */
	playheadPercent: number;
	isScrubbing: boolean;
	onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
	onPointerMove: (event: ReactPointerEvent<HTMLElement>) => void;
	onPointerUp: (event: ReactPointerEvent<HTMLElement>) => void;
	onPointerLeave: (event: ReactPointerEvent<HTMLElement>) => void;
	onPointerCancel: (event: ReactPointerEvent<HTMLElement>) => void;
}

export const MEDIA_PLAYER_SEEK_PIN_GRAB_SELECTOR = "[data-seek-pin-grab]";

export function isMediaPlayerSeekPinGrabEvent(
	target: EventTarget | null,
): boolean {
	if (!(target instanceof Element)) {
		return false;
	}
	return target.closest(MEDIA_PLAYER_SEEK_PIN_GRAB_SELECTOR) != null;
}

export function resolveMediaPlayerScrubPointerPercent(options: {
	clientX: number;
	bounds: Pick<DOMRect, "left" | "width">;
	grabOffsetPx?: number;
}): number {
	const width = options.bounds.width;
	if (width <= 0) {
		return 0;
	}
	const x = options.clientX - (options.grabOffsetPx ?? 0) - options.bounds.left;
	return Math.max(0, Math.min(100, (x / width) * 100));
}

export function resolveMediaPlayerScrubGrabOffsetPx(options: {
	clientX: number;
	bounds: Pick<DOMRect, "left" | "width">;
	playheadPercent: number;
}): number {
	const playheadX =
		options.bounds.left +
		(options.playheadPercent / 100) * options.bounds.width;
	return options.clientX - playheadX;
}

function resolvePercentFromPointer(
	event: ReactPointerEvent<HTMLElement>,
	bounds: DOMRect,
	grabOffsetPx = 0,
): number {
	return resolveMediaPlayerScrubPointerPercent({
		clientX: event.clientX,
		bounds,
		grabOffsetPx,
	});
}

function resolveScrubTrackBounds(
	event: ReactPointerEvent<HTMLElement>,
	trackBoundsRef?: RefObject<HTMLElement | null>,
): DOMRect {
	const element = trackBoundsRef?.current ?? event.currentTarget;
	return element.getBoundingClientRect();
}

export function shouldClearMediaPlayerScrubLatch(
	progressPercent: number,
	committedPercent: number,
	epsilon = MEDIA_PLAYER_SCRUB_LATCH_EPSILON,
): boolean {
	return Math.abs(progressPercent - committedPercent) <= epsilon;
}

/**
 * Abandon the post-scrub playhead latch when authoritative progress leaves the
 * scrub path (skip / rewind / card nav / ff). Keep the latch only while progress
 * is still catching up from the pre-commit value toward the commit.
 */
export function shouldAbandonMediaPlayerScrubLatch(options: {
	progressPercent: number;
	committedPercent: number;
	progressAtLatch: number;
	epsilon?: number;
}): boolean {
	const {
		progressPercent,
		committedPercent,
		progressAtLatch,
		epsilon = MEDIA_PLAYER_SCRUB_LATCH_EPSILON,
	} = options;
	if (
		shouldClearMediaPlayerScrubLatch(
			progressPercent,
			committedPercent,
			epsilon,
		)
	) {
		return true;
	}
	const latchToCommit = committedPercent - progressAtLatch;
	const latchToProgress = progressPercent - progressAtLatch;
	if (Math.abs(latchToCommit) <= epsilon) {
		return Math.abs(progressPercent - committedPercent) > epsilon;
	}
	const sameDirection = latchToCommit * latchToProgress >= 0;
	const withinScrubSpan =
		Math.abs(latchToProgress) <= Math.abs(latchToCommit) + epsilon;
	if (sameDirection && withinScrubSpan) {
		return false;
	}
	return true;
}

export function resolveMediaPlayerScrubPlayheadPercent(options: {
	isScrubbing: boolean;
	scrubPercent: number;
	progressPercent: number;
	committedScrubPercent: number | null;
}): number {
	if (options.isScrubbing) {
		return options.scrubPercent;
	}
	if (options.committedScrubPercent != null) {
		return options.committedScrubPercent;
	}
	return options.progressPercent;
}

export function useMediaPlayerScrub({
	progressPercent,
	maxSeekPercent,
	scrubClampPercent,
	interactive,
	seekEpoch = 0,
	trackBoundsRef,
	onPause,
	onSeekCommit,
	onSeekPreview,
	onSeekRejected,
}: UseMediaPlayerScrubOptions): UseMediaPlayerScrubResult {
	const effectiveClampPercent = scrubClampPercent ?? maxSeekPercent;
	const captureRef = useRef<HTMLElement | null>(null);
	const [isScrubbing, setIsScrubbing] = useState(false);
	const [scrubPercent, setScrubPercent] = useState(progressPercent);
	const [committedScrubPercent, setCommittedScrubPercent] = useState<
		number | null
	>(null);
	const scrubPercentRef = useRef(scrubPercent);
	/** Authoritative progress when the latch was taken (anti-flicker baseline). */
	const progressAtLatchRef = useRef<number | null>(null);
	const lastSeenSeekEpochRef = useRef(seekEpoch);
	const grabOffsetPxRef = useRef(0);

	useEffect(() => {
		scrubPercentRef.current = scrubPercent;
	}, [scrubPercent]);

	useEffect(() => {
		if (lastSeenSeekEpochRef.current === seekEpoch) {
			return;
		}
		lastSeenSeekEpochRef.current = seekEpoch;
		// Own scrub commit also bumps seekEpoch. Keep the display latch until
		// live progress catches the commit (or a skip/rewind abandons it).
		if (isScrubbing || committedScrubPercent == null) {
			return;
		}
		const progressAtLatch = progressAtLatchRef.current ?? progressPercent;
		if (
			shouldAbandonMediaPlayerScrubLatch({
				progressPercent,
				committedPercent: committedScrubPercent,
				progressAtLatch,
			})
		) {
			progressAtLatchRef.current = null;
			setCommittedScrubPercent(null);
		}
	}, [seekEpoch, isScrubbing, committedScrubPercent, progressPercent]);

	useEffect(() => {
		if (isScrubbing || committedScrubPercent == null) {
			return;
		}
		const progressAtLatch = progressAtLatchRef.current ?? progressPercent;
		if (
			shouldAbandonMediaPlayerScrubLatch({
				progressPercent,
				committedPercent: committedScrubPercent,
				progressAtLatch,
			})
		) {
			progressAtLatchRef.current = null;
			setCommittedScrubPercent(null);
		}
	}, [progressPercent, isScrubbing, committedScrubPercent]);

	useEffect(() => {
		if (!isScrubbing && committedScrubPercent == null) {
			setScrubPercent(progressPercent);
		}
	}, [progressPercent, isScrubbing, committedScrubPercent]);

	const commitPercent = useCallback(
		(requestedPercent: number) => {
			const { clampedPercent, wasRejected } = clampMediaPlayerSeekPercent(
				requestedPercent,
				effectiveClampPercent,
			);
			if (wasRejected) {
				onSeekRejected?.();
			}
			scrubPercentRef.current = clampedPercent;
			if (progressAtLatchRef.current == null) {
				progressAtLatchRef.current = progressPercent;
			}
			setScrubPercent(clampedPercent);
			setCommittedScrubPercent(clampedPercent);
			onSeekCommit(clampedPercent);
		},
		[effectiveClampPercent, onSeekCommit, onSeekRejected, progressPercent],
	);

	const onPointerDown = useCallback(
		(event: ReactPointerEvent<HTMLElement>) => {
			if (!interactive) {
				return;
			}
			const target = event.currentTarget;
			captureRef.current = target;
			onPause();
			progressAtLatchRef.current = progressPercent;
			setCommittedScrubPercent(null);
			setIsScrubbing(true);
			target.setPointerCapture(event.pointerId);
			const bounds = resolveScrubTrackBounds(event, trackBoundsRef);
			const grabFromPin = isMediaPlayerSeekPinGrabEvent(event.target);
			if (grabFromPin) {
				grabOffsetPxRef.current = resolveMediaPlayerScrubGrabOffsetPx({
					clientX: event.clientX,
					bounds,
					playheadPercent: progressPercent,
				});
			} else {
				grabOffsetPxRef.current = 0;
			}
			const next = resolvePercentFromPointer(
				event,
				bounds,
				grabOffsetPxRef.current,
			);
			const { clampedPercent, wasRejected } = clampMediaPlayerSeekPercent(
				next,
				effectiveClampPercent,
			);
			if (wasRejected) {
				onSeekRejected?.();
			}
			scrubPercentRef.current = clampedPercent;
			setScrubPercent(clampedPercent);
			onSeekPreview?.(clampedPercent);
		},
		[
			interactive,
			effectiveClampPercent,
			onPause,
			onSeekPreview,
			onSeekRejected,
			trackBoundsRef,
			progressPercent,
		],
	);

	const onPointerMove = useCallback(
		(event: ReactPointerEvent<HTMLElement>) => {
			if (!interactive || !isScrubbing || !captureRef.current) {
				return;
			}
			const bounds = resolveScrubTrackBounds(event, trackBoundsRef);
			const next = resolvePercentFromPointer(
				event,
				bounds,
				grabOffsetPxRef.current,
			);
			const { clampedPercent, wasRejected } = clampMediaPlayerSeekPercent(
				next,
				effectiveClampPercent,
			);
			if (wasRejected) {
				onSeekRejected?.();
			}
			scrubPercentRef.current = clampedPercent;
			setScrubPercent(clampedPercent);
			onSeekPreview?.(clampedPercent);
		},
		[
			interactive,
			isScrubbing,
			effectiveClampPercent,
			onSeekPreview,
			onSeekRejected,
			trackBoundsRef,
		],
	);

	const finishScrub = useCallback(
		(event: ReactPointerEvent<HTMLElement>) => {
			if (!isScrubbing) {
				return;
			}
			if (captureRef.current?.hasPointerCapture(event.pointerId)) {
				captureRef.current.releasePointerCapture(event.pointerId);
			}
			grabOffsetPxRef.current = 0;
			commitPercent(scrubPercentRef.current);
			setIsScrubbing(false);
		},
		[commitPercent, isScrubbing],
	);

	const onPointerUp = useCallback(
		(event: ReactPointerEvent<HTMLElement>) => {
			finishScrub(event);
		},
		[finishScrub],
	);

	const onPointerCancel = useCallback(
		(event: ReactPointerEvent<HTMLElement>) => {
			finishScrub(event);
		},
		[finishScrub],
	);

	const onPointerLeave = useCallback(
		(event: ReactPointerEvent<HTMLElement>) => {
			if (captureRef.current?.hasPointerCapture(event.pointerId)) {
				return;
			}
			finishScrub(event);
		},
		[finishScrub],
	);

	const playheadPercent = resolveMediaPlayerScrubPlayheadPercent({
		isScrubbing,
		scrubPercent,
		progressPercent,
		committedScrubPercent,
	});

	return {
		playheadPercent,
		isScrubbing,
		onPointerDown,
		onPointerMove,
		onPointerUp,
		onPointerLeave,
		onPointerCancel,
	};
}
